import React, { useEffect, useMemo, useState } from "react";

const toLocalInput = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = pad(dt.getMonth() + 1);
  const dd = pad(dt.getDate());
  const hh = pad(dt.getHours());
  const min = pad(dt.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const normalizeToLocalInput = (val) => {
  if (!val) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(val)) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val).slice(0, 16) : toLocalInput(d);
};

const ScheduleMaintenanceModal = ({
  open,
  onClose,
  onSubmit,
  defaultStart,
  defaultEnd,
  title = "Schedule Maintenance",
}) => {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [saving, setSaving] = useState(false);

  const nowLocal = useMemo(() => toLocalInput(new Date()), []);

  useEffect(() => {
    if (open) {
      setStart(normalizeToLocalInput(defaultStart));
      setEnd(normalizeToLocalInput(defaultEnd));
    }
  }, [open, defaultStart, defaultEnd]);

  if (!open) return null;

  const isInvalid = (() => {
    if (!start || !end) return true;
    const s = new Date(start);
    const e = new Date(end);
    const now = new Date();
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return true;
    if (s <= now) return true;
    if (e <= s) return true;
    return false;
  })();

  const submit = async () => {
    if (isInvalid) return;
    const s = new Date(start);
    const e = new Date(end);
    try {
      setSaving(true);
      const maybe = onSubmit?.(s.toISOString(), e.toISOString());
      if (maybe && typeof maybe.then === "function") {
        await maybe;
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-bg-black/40">
      <div className="tw-bg-white tw-w-[520px] tw-rounded-2xl tw-shadow-xl tw-p-6">
        <h3 className="tw-text-lg tw-font-semibold tw-mb-4">{title}</h3>

        <div className="tw-space-y-4">
          <div className="tw-flex tw-flex-col">
            <label className="tw-text-sm tw-text-gray-600 tw-mb-1">
              Start time
            </label>
            <input
              type="datetime-local"
              className="tw-border tw-rounded tw-px-3 tw-py-2"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              disabled={saving}
              min={nowLocal}
            />
          </div>

          <div className="tw-flex tw-flex-col">
            <label className="tw-text-sm tw-text-gray-600 tw-mb-1">
              End time
            </label>
            <input
              type="datetime-local"
              className="tw-border tw-rounded tw-px-3 tw-py-2"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              disabled={saving}
              min={start || nowLocal}
            />
          </div>
        </div>

        <div className="tw-flex tw-justify-end tw-gap-3 tw-mt-6">
          <button
            className="tw-border tw-rounded tw-px-4 tw-py-2 hover:tw-bg-gray-50"
            onClick={onClose}
            disabled={saving}
            style={saving ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
          >
            Cancel
          </button>

          <button
            className={`tw-rounded tw-px-4 tw-py-2 tw-text-white ${
              saving || isInvalid
                ? "tw-bg-gray-700 tw-opacity-60 tw-cursor-not-allowed"
                : "tw-bg-gray-900 hover:tw-bg-black"
            }`}
            onClick={submit}
            disabled={saving || isInvalid}
          >
            <span className="tw-inline-flex tw-items-center">
              {saving && (
                <svg
                  className="tw-animate-spin tw-h-4 tw-w-4 tw-mr-2"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="tw-opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="tw-opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
              )}
              Save
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleMaintenanceModal;
